import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FileUp, Search } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import ImportWizard from './ImportWizard';
import SetlistDetail from './SetlistDetail';
import { errorMessage, listLibrary } from './libraryApi';
import { formatServiceDate, pluralize, STATUS_LABELS, STATUS_STYLES } from './libraryFormat';

const EMPTY_FILTERS = { q: '', occasion: '', song: '', status: '', date_from: '', date_to: '', all_versions: false };
const PAGE = 50;

const toParams = (filters, skip) => ({
  ...Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== '' && value !== false)),
  skip, limit: PAGE,
});

const LibraryRow = ({ item, onOpen }) => (
  <li>
    <button type="button" onClick={() => onOpen(item.id)} aria-label={`Open setlist ${item.occasion || 'zangdienst'} ${formatServiceDate(item.service_date, item.date_certain)}`}
      className="w-full rounded-md border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:border-teal-300 hover:shadow-md">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <p className="font-semibold text-slate-900">{formatServiceDate(item.service_date, item.date_certain)}</p>
          <p className="break-words text-sm text-slate-600">{item.occasion || 'Zangdienst'}</p>
        </div>
        <div className="flex flex-wrap gap-1.5 sm:justify-end">
          <Badge className={STATUS_STYLES[item.status]}>{STATUS_LABELS[item.status]}</Badge>
          <Badge variant="secondary">{pluralize(item.item_count, 'lied', 'liederen')}</Badge>
          {item.version_count > 1 && <Badge className="bg-amber-50 text-amber-800">{item.version_count} versies</Badge>}
          {item.unlinked_count > 0 && <Badge variant="secondary">{item.unlinked_count} niet gekoppeld</Badge>}
        </div>
      </div>
      <ol className="mt-2 list-decimal space-y-0.5 pl-5 text-sm text-slate-700 sm:columns-2">
        {item.titles.slice(0, 8).map((title, index) => <li key={`${index}-${title}`} className="break-words">{title}</li>)}
      </ol>
      {item.titles.length > 8 && <p className="mt-1 text-xs text-slate-500">en nog {item.titles.length - 8} meer…</p>}
    </button>
  </li>
);

/** Worship -> Setlists -> Bibliotheek: browse and search historical setlists, import new ones, reuse them. */
const SetlistLibrary = ({ canManage }) => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setApplied(filters), 300);
    return () => clearTimeout(timer);
  }, [filters]);

  const load = useCallback(async (skip = 0) => {
    setLoading(true);
    try {
      const page = await listLibrary(toParams(applied, skip));
      setItems((current) => (skip ? [...current, ...page.items] : page.items));
      setHasMore(page.has_more);
      setError('');
    } catch (err) {
      setError(errorMessage(err, 'De bibliotheek kon niet worden geladen'));
    } finally {
      setLoading(false);
    }
  }, [applied]);

  useEffect(() => { load(0); }, [load]);

  const set = (key) => (event) => setFilters({ ...filters, [key]: event.target.type === 'checkbox' ? event.target.checked : event.target.value });
  const filtered = JSON.stringify(applied) !== JSON.stringify(EMPTY_FILTERS);
  const field = 'h-11 min-w-0 rounded-md border border-slate-200 bg-white px-3 text-sm';

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <nav aria-label="Kruimelpad" className="text-sm text-slate-500"><Link to="/worship/setlists" className="underline">Setlists</Link> › Bibliotheek</nav>
          <h1 className="text-2xl font-bold text-slate-900">Setlistbibliotheek</h1>
          <p className="mt-1 text-slate-500">Eerdere zangdiensten om op terug te kijken en opnieuw te gebruiken.</p>
        </div>
        {canManage && <Button type="button" variant="outline" className="min-h-11" onClick={() => setImportOpen(true)}><FileUp className="mr-2 h-4 w-4" />Historische setlists importeren</Button>}
      </div>

      <form role="search" aria-label="Zoek in de bibliotheek" onSubmit={(event) => event.preventDefault()} className="grid gap-2 rounded-md border border-slate-200 bg-white p-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="relative sm:col-span-2">
          <Search className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
          <Input aria-label="Zoek op titel, lied of gelegenheid" className="h-11 pl-9" placeholder="Zoek op titel, lied of gelegenheid" value={filters.q} onChange={set('q')} />
        </div>
        <Input aria-label="Filter op gelegenheid" className="h-11" placeholder="Gelegenheid" value={filters.occasion} onChange={set('occasion')} />
        <Input aria-label="Filter op lied" className="h-11" placeholder="Lied" value={filters.song} onChange={set('song')} />
        <label className="flex items-center gap-2 text-sm text-slate-600">Van<Input type="date" aria-label="Datum vanaf" className="h-11" value={filters.date_from} onChange={set('date_from')} /></label>
        <label className="flex items-center gap-2 text-sm text-slate-600">Tot<Input type="date" aria-label="Datum tot en met" className="h-11" value={filters.date_to} onChange={set('date_to')} /></label>
        <select aria-label="Filter op status" className={field} value={filters.status} onChange={set('status')}>
          <option value="">Alle statussen</option>
          {Object.entries(STATUS_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
        <label className="flex min-h-11 items-center gap-2 text-sm text-slate-700"><input type="checkbox" className="h-5 w-5" checked={filters.all_versions} onChange={set('all_versions')} />Toon alle versies</label>
        {filtered && <Button type="button" variant="outline" className="min-h-11" onClick={() => setFilters(EMPTY_FILTERS)}>Filters wissen</Button>}
      </form>

      {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
      <p className="text-sm text-slate-500" aria-live="polite">{loading ? 'Laden…' : `${pluralize(items.length, 'resultaat', 'resultaten')}${hasMore ? ' of meer' : ''}`}</p>
      {!loading && !items.length && !error && (
        <div className="py-10 text-center">
          <p className="font-medium text-slate-800">{filtered ? 'Geen setlists gevonden' : 'Nog geen historische setlists'}</p>
          <p className="mt-1 text-sm text-slate-500">{filtered ? 'Pas je filters aan of wis ze.' : canManage ? 'Importeer een archief om te beginnen.' : 'Een Worship-beheerder kan een archief importeren.'}</p>
        </div>
      )}
      <ul className="space-y-3">{items.map((item) => <LibraryRow key={item.id} item={item} onOpen={setOpenId} />)}</ul>
      {hasMore && <Button type="button" variant="outline" className="min-h-11 w-full sm:w-auto" disabled={loading} onClick={() => load(items.length)}>Meer laden</Button>}

      {openId && <SetlistDetail id={openId} canManage={canManage} onClose={() => setOpenId(null)} onChanged={() => load(0)}
        onReused={() => { setOpenId(null); navigate('/worship/setlists'); }} />}
      {canManage && <ImportWizard open={importOpen} onOpenChange={setImportOpen} onImported={() => load(0)} />}
    </div>
  );
};

export default SetlistLibrary;
